import styled from '@emotion/styled';
import {
    Alert,
    Card,
    FormControl,
    FormControlLabel,
    FormGroup,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
} from '@mui/material';
import Button from '@shared/components/core/button/CoreButton';
import DeleteIconButton from '@shared/components/core/button/DeleteIconButton';
import TextButton from '@shared/components/core/button/TextButton';
import HorizontalFieldContainer from '@shared/components/core/field/HorizontalFieldContainer';
import StyledTextField from '@shared/components/core/field/StyledTextField';
import ChipButtonContainer from '@shared/components/core/layouts/ChipButtonContainer';
import { FieldLabel, FieldSetLabel } from '@shared/components/core/text/FormsTypography';
import { LabelLarge } from '@shared/components/core/text/Typography';
import { getDefaultKeyMapping, getKeyValue } from '@shared/constants/Keybindings';
import { createUUID } from '@shared/constants/Keys';
import EditProfileContainer from '@shared/hooks/state/EditProfileContainer';
import GlobalCustomControlsContainer from '@shared/hooks/state/GlobalCustomControlsContainer';
import {
    MacroDisplayEvent,
    MacroEventType,
    hasHoldTime,
    macroEventTypes,
} from '@shared/models/deviceProfiles/MacroEvent';
import { MacroDisplayModel } from '@shared/models/deviceProfiles/MacroModel';
import _ from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import JoystickStep from './StepJoystickMove';
import StepMouseMove from './StepMouseMove';

// TODO Refactor to match what we have done on mobile and with input fields Issue #487

interface MacroStepEditorProps {
    macro: MacroDisplayModel;
    step: MacroDisplayEvent;
    stepIndex: number;
    isPreview?: boolean;
    onChange: (macro: MacroDisplayModel) => void;
}

type KeyPressType = 'tap' | 'latch' | 'hold' | 'toggle' | 'release';

const MacroStepEditor: React.FC<MacroStepEditorProps> = ({ macro, step, stepIndex, isPreview, onChange }) => {
    const { t } = useTranslation();
    const globalCustomControlsContainer = GlobalCustomControlsContainer.useContainer();
    const editProfileContainer = EditProfileContainer.useContainer();
    const locale = editProfileContainer.defaultLocaleRef.current;
    const defaultKeyMapping = getDefaultKeyMapping(locale, editProfileContainer.originalProfileRef.current?.platform);

    const [showStepOptions, setShowStepOptions] = useState<boolean>(false);
    const handleMoveStepUp = (eventIndex: number) => {
        if (eventIndex == 0) return;
        const temp = macro.events[eventIndex - 1];
        macro.events[eventIndex - 1] = macro.events[eventIndex];
        macro.events[eventIndex] = temp;
        onChange({ ...macro });
    };
    const getCustomControlAllowsHold = (controlId: string | undefined) => {
        const controls =
            editProfileContainer.availableCustomControls?.length > 0
                ? editProfileContainer.availableCustomControls
                : (globalCustomControlsContainer.profileCustomControls ?? []);
        for (const deviceControls of controls) {
            const control = deviceControls.controls.find((c) => c.id === controlId);
            if (control) {
                return control.allowsHold;
            }
        }
        return false;
    };
    const isDeviceAvailable = (deviceTypeId: string | undefined) => {
        const controls =
            editProfileContainer.availableCustomControls?.length > 0
                ? editProfileContainer.availableCustomControls
                : (globalCustomControlsContainer.profileCustomControls ?? []);
        if (!deviceTypeId || controls.length === 0) {
            return true;
        }
        return controls.some((device) => device.deviceTypeId === deviceTypeId);
    };
    const handleMoveStepDown = (eventIndex: number) => {
        if (eventIndex == macro.events.length - 1) return;
        const temp = macro.events[eventIndex + 1];
        macro.events[eventIndex + 1] = macro.events[eventIndex];
        macro.events[eventIndex] = temp;
        onChange({ ...macro });
    };
    const handleCopyStep = (eventIndex: number) => {
        const clonedMacro = _.cloneDeep(macro);
        macro.events.push({ ...clonedMacro.events[eventIndex] });
        onChange({ ...macro });
    };
    const handleInsertStep = (eventIndex: number) => {
        macro.events.splice(eventIndex + 1, 0, {
            eventType: 'KeyPress',
            keys: [''],
            holdTimeMilliseconds: 0,
        });
        onChange({ ...macro });
    };
    const handleRemoveStep = (eventIndex: number) => {
        macro.events.splice(eventIndex, 1);
        onChange({ ...macro });
    };

    const handleEventFieldTypeChange = (eventIndex: number, value: string) => {
        macro.events[eventIndex].eventType = value as MacroEventType;
        onChange({ ...macro });
    };
    const handleEventHoldChange = (eventIndex: number, value: string) => {
        macro.events[eventIndex].holdTimeMilliseconds = value ? Number(value) : undefined;
        onChange({ ...macro });
    };
    const handleEventTypeChange = (eventIndex: number, value: string) => {
        macro.events[eventIndex].typedPhrase = value;
        onChange({ ...macro });
    };
    const handleEventAudioChange = (eventIndex: number, value: string) => {
        macro.events[eventIndex].outputSpeech = value;
        onChange({ ...macro });
    };
    const handleEventDeviceTypeCustomActionIdChange = (eventIndex: number, event: any) => {
        if (editProfileContainer.availableCustomControls || globalCustomControlsContainer.profileCustomControls) {
            const controls =
                editProfileContainer.availableCustomControls.length > 0
                    ? editProfileContainer.availableCustomControls
                    : globalCustomControlsContainer.profileCustomControls
                      ? globalCustomControlsContainer.profileCustomControls
                      : [];
            const deviceTypeId = controls.find((control) =>
                control.controls.some((c) => c.id === event.target.value)
            )?.deviceTypeId;
            macro.events[eventIndex].deviceTypeCustomActionId = event.target.value;
            macro.events[eventIndex].deviceTypeId = deviceTypeId;
            onChange({ ...macro });
        }
    };

    const handleAddKey = (eventIndex: number) => {
        if (macro?.events?.[eventIndex]?.keys) {
            macro?.events?.[eventIndex]?.keys?.push('');
        }
        onChange({ ...macro });
    };

    const handleKeyValueChange = (eventIndex: number, keyIndex: number, value: string) => {
        if (macro.events && macro.events[eventIndex] && macro.events[eventIndex].keys) {
            if (macro.events?.[eventIndex]?.keys?.[keyIndex] !== undefined) {
                macro.events[eventIndex].keys![keyIndex] = value;
            }
        }
        onChange({ ...macro });
    };

    const handleRemoveKey = (eventIndex: number, keyIndex: number) => {
        if (macro?.events?.[eventIndex]?.keys) {
            macro.events[eventIndex].keys!.splice(keyIndex, 1);
        }
        onChange({ ...macro });
    };
    const uuId = createUUID();

    const handleKeyPressTypeChange = (eventIndex: number, value: KeyPressType) => {
        if (value == 'tap') {
            macro.events[eventIndex].holdTimeMilliseconds = undefined;
            macro.events[eventIndex].isKeyLatch = false;
            macro.events[eventIndex].isCustomToggle = false;
            macro.events[eventIndex].isCustomRelease = false;
        } else if (value == 'latch') {
            macro.events[eventIndex].holdTimeMilliseconds = undefined;
            macro.events[eventIndex].isKeyLatch = true;
            macro.events[eventIndex].isCustomToggle = false;
            macro.events[eventIndex].isCustomRelease = false;
        } else if (value == 'hold') {
            macro.events[eventIndex].holdTimeMilliseconds = 1000;
            macro.events[eventIndex].isKeyLatch = false;
            macro.events[eventIndex].isCustomToggle = false;
            macro.events[eventIndex].isCustomRelease = false;
        } else if (value == 'toggle') {
            macro.events[eventIndex].isKeyLatch = false;
            macro.events[eventIndex].holdTimeMilliseconds = 0;
            macro.events[eventIndex].isCustomToggle = true;
            macro.events[eventIndex].isCustomRelease = false;
        } else if (value == 'release') {
            macro.events[eventIndex].isKeyLatch = false;
            macro.events[eventIndex].holdTimeMilliseconds = 0;
            macro.events[eventIndex].isCustomToggle = false;
            macro.events[eventIndex].isCustomRelease = true;
        }
        onChange({ ...macro });
    };
    const getKeyPressType = (eventIndex: number): KeyPressType => {
        if (macro.events[eventIndex].isCustomToggle) {
            return 'toggle';
        }
        if (macro.events[eventIndex].isCustomRelease) {
            return 'release';
        }
        if (macro.events[eventIndex].isKeyLatch) {
            return 'latch';
        }
        if (macro.events[eventIndex].holdTimeMilliseconds != null) {
            return 'hold';
        }
        return 'tap';
    };
    return (
        <>
            <StepCard>
                <FormControl fullWidth>
                    <InputLabel id={`step-${stepIndex}`}>{t('common:event.stepType')}</InputLabel>
                    <Select
                        disabled={isPreview}
                        labelId={`step-${stepIndex}`}
                        value={step.eventType}
                        label={t('common:event.stepType')}
                        onChange={(e) => handleEventFieldTypeChange(stepIndex, e.target.value)}>
                        {macroEventTypes.map((c) => (
                            <MenuItem key={c.eventType} value={c.eventType}>
                                {t(c.displayName)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {step.eventType == 'DeviceTypeCustomAction' && (
                    <>
                        <FieldLabel>{t('common:event.deviceTypeCustomAction.title')}</FieldLabel>
                        {!isDeviceAvailable(step.deviceTypeId) && step.deviceTypeId ? (
                            <>
                                <StyledTextField
                                    fullWidth
                                    label={t('common:event.deviceTypeCustomAction.title')}
                                    disabled={true}
                                    value={step.deviceTypeCustomActionId || ''}
                                    id={'custom_' + uuId}
                                    slotProps={{
                                        htmlInput: {
                                            readOnly: true,
                                        },
                                    }}
                                />
                                <Alert severity="info">
                                    {t('common:customControls.noDevice')}
                                    <TextButton to={`/extensions/new/${step.deviceTypeId}`} startIcon="deviceManager">
                                        {t('common:device.add.action')}
                                    </TextButton>
                                </Alert>
                            </>
                        ) : (
                            <FormControl fullWidth>
                                <InputLabel id={'custom_' + uuId}>
                                    {t('common:event.deviceTypeCustomAction.title')}
                                </InputLabel>
                                <Select
                                    labelId={'custom_' + uuId}
                                    fullWidth
                                    value={step.deviceTypeCustomActionId || ''}
                                    label={t('common:event.deviceTypeCustomAction.title')}
                                    onChange={(event) => {
                                        handleEventDeviceTypeCustomActionIdChange(stepIndex, event);
                                    }}>
                                    {editProfileContainer.availableCustomControls?.flatMap((d) => [
                                        <DeviceSubheader key={d.deviceTypeId}>{d.name}</DeviceSubheader>,
                                        d.controls.map((c) => (
                                            <MenuItem key={c.id} value={c.id} data-device={d.deviceTypeId}>
                                                {c.name}
                                            </MenuItem>
                                        )),
                                    ])}
                                </Select>
                            </FormControl>
                        )}
                        {getCustomControlAllowsHold(step.deviceTypeCustomActionId) && (
                            <fieldset>
                                <FieldSetLabel>{t('common:event.keyPress.timing')}</FieldSetLabel>
                                <RadioGroup
                                    row
                                    name={`${stepIndex}-key-press-type`}
                                    defaultValue="right"
                                    onChange={(e) =>
                                        handleKeyPressTypeChange(stepIndex, e.target.value as KeyPressType)
                                    }
                                    value={getKeyPressType(stepIndex)}>
                                    <StyledFormControlLabel
                                        value="tap"
                                        control={<Radio disabled={isPreview} />}
                                        label={t('common:event.keyPress.tapLong')}
                                    />
                                    <StyledFormControlLabel
                                        value="hold"
                                        control={<Radio disabled={isPreview} />}
                                        label={t('common:event.keyPress.holdLong')}
                                    />
                                    <StyledFormControlLabel
                                        value="latch"
                                        control={<Radio disabled={isPreview} />}
                                        label={t('common:event.keyPress.latchLong')}
                                    />
                                    <StyledFormControlLabel
                                        value="toggle"
                                        control={<Radio disabled={isPreview} />}
                                        label={t('common:event.keyPress.toggleLong')}
                                    />
                                    <StyledFormControlLabel
                                        value="release"
                                        control={<Radio disabled={isPreview} />}
                                        label={t('common:event.keyPress.releaseLong')}
                                    />
                                </RadioGroup>
                                {getCustomControlAllowsHold(step.deviceTypeCustomActionId) &&
                                    getKeyPressType(stepIndex) === 'hold' && (
                                        <StyledTextField
                                            fullWidth
                                            label={t('common:event.hold')}
                                            disabled={isPreview}
                                            type="number"
                                            value={step.holdTimeMilliseconds ?? 0}
                                            id={`${stepIndex}hold`}
                                            placeholder={t('common:event.hold')}
                                            onChange={(e) => handleEventHoldChange(stepIndex, e.target.value)}
                                        />
                                    )}
                            </fieldset>
                        )}
                    </>
                )}
                {step.eventType == 'KeyPress' && (
                    <>
                        <FieldLabel>{t('common:event.keyPress.fieldSet')}</FieldLabel>
                        {step.keys?.map((k, ki) => (
                            <HorizontalFieldContainer key={uuId + ki}>
                                <FormControl fullWidth>
                                    <InputLabel id={'key_' + uuId + ki}>{t('common:event.keyPress.label')}</InputLabel>

                                    <Select
                                        labelId={'key_' + uuId + ki}
                                        value={getKeyValue(k, locale)}
                                        label={t('common:event.keyPress.label')}
                                        onChange={(e) => handleKeyValueChange(stepIndex, ki, e.target.value)}>
                                        {defaultKeyMapping.map((c) => (
                                            <MenuItem key={c.key} value={c.key}>
                                                {c.displayName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <DeleteIconButton
                                    disabled={isPreview}
                                    onClick={() => handleRemoveKey(stepIndex, ki)}
                                    showLabel={false}>
                                    {t('common:event.keyPress.remove')}
                                </DeleteIconButton>
                            </HorizontalFieldContainer>
                        ))}
                        <ChipButtonContainer>
                            <TextButton
                                startIcon="touchpad"
                                disabled={isPreview}
                                onClick={() => handleAddKey(stepIndex)}>
                                {t('common:event.keyPress.add')}
                            </TextButton>
                        </ChipButtonContainer>
                        <fieldset>
                            <FieldSetLabel>{t('common:event.keyPress.timing')}</FieldSetLabel>
                            <RadioGroup
                                row
                                name={`${stepIndex}-key-press-type`}
                                defaultValue="right"
                                onChange={(e) => handleKeyPressTypeChange(stepIndex, e.target.value as KeyPressType)}
                                value={getKeyPressType(stepIndex)}>
                                <StyledFormControlLabel
                                    value="tap"
                                    control={<Radio disabled={isPreview} />}
                                    label={t('common:event.keyPress.tapLong')}
                                />
                                <StyledFormControlLabel
                                    value="hold"
                                    control={<Radio disabled={isPreview} />}
                                    label={t('common:event.keyPress.holdLong')}
                                />
                                <StyledFormControlLabel
                                    value="latch"
                                    control={<Radio disabled={isPreview} />}
                                    label={t('common:event.keyPress.latchLong')}
                                />
                            </RadioGroup>
                        </fieldset>
                    </>
                )}
                {step.eventType == 'KeyRelease' && (
                    <>
                        <FieldLabel>{t('common:event.keyRelease.title')}</FieldLabel>
                        {step.keys?.map((k, ki) => (
                            <HorizontalFieldContainer key={uuId + ki}>
                                <FormControl fullWidth>
                                    <InputLabel id={'release' + uuId + ki}>
                                        {t('common:event.keyRelease.title')}
                                    </InputLabel>

                                    <Select
                                        labelId={'release' + uuId + ki}
                                        value={getKeyValue(k, locale)}
                                        label={t('common:event.keyRelease.title')}
                                        onChange={(e) => handleKeyValueChange(stepIndex, ki, e.target.value)}>
                                        {defaultKeyMapping.map((c) => (
                                            <MenuItem key={c.key} value={c.key}>
                                                {c.displayName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <DeleteIconButton
                                    disabled={isPreview}
                                    onClick={() => handleRemoveKey(stepIndex, ki)}
                                    showLabel={false}>
                                    {t('common:event.keyRelease.remove')}
                                </DeleteIconButton>
                            </HorizontalFieldContainer>
                        ))}
                        <ChipButtonContainer>
                            <TextButton
                                startIcon="touchpad"
                                disabled={isPreview}
                                onClick={() => handleAddKey(stepIndex)}>
                                {t('common:event.keyRelease.add')}
                            </TextButton>
                        </ChipButtonContainer>
                    </>
                )}
                {step.eventType == 'KeyToggle' && (
                    <>
                        <FieldLabel>{t('common:event.keyToggle.title')}</FieldLabel>
                        {step.keys?.map((k, ki) => (
                            <HorizontalFieldContainer key={ki}>
                                <FormControl fullWidth>
                                    <InputLabel id={`key-${ki}`}>{t('common:event.keyToggle.title')}</InputLabel>

                                    <Select
                                        labelId={`key-${ki}`}
                                        value={getKeyValue(k, locale)}
                                        label={t('common:event.keyToggle.selected')}
                                        onChange={(e) => handleKeyValueChange(stepIndex, ki, e.target.value)}>
                                        {defaultKeyMapping.map((c) => (
                                            <MenuItem key={c.key} value={c.key}>
                                                {c.displayName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <DeleteIconButton
                                    disabled={isPreview}
                                    onClick={() => handleRemoveKey(stepIndex, ki)}
                                    showLabel={false}>
                                    {t('common:event.keyToggle.remove')}
                                </DeleteIconButton>
                            </HorizontalFieldContainer>
                        ))}
                        <ChipButtonContainer>
                            <TextButton
                                startIcon="touchpad"
                                disabled={isPreview}
                                onClick={() => handleAddKey(stepIndex)}>
                                {t('common:event.keyToggle.add')}
                            </TextButton>
                        </ChipButtonContainer>
                    </>
                )}
                {step.eventType == 'Type' && (
                    <>
                        <StyledTextField
                            fullWidth
                            multiline
                            minRows={3}
                            label={t('common:event.type.field')}
                            disabled={isPreview}
                            type="text"
                            value={step.typedPhrase}
                            id={uuId}
                            placeholder={t('common:event.type.placeholder')}
                            onChange={(e) => handleEventTypeChange(stepIndex, e.target.value)}
                        />
                    </>
                )}
                {step.eventType == 'PlayAudio' && (
                    <>
                        <StyledTextField
                            fullWidth
                            label={t('common:event.playAudio.title')}
                            disabled={isPreview}
                            value={step.outputSpeech}
                            id={uuId}
                            placeholder={t('common:event.playAudio.placeholder')}
                            onChange={(e) => handleEventAudioChange(stepIndex, e.target.value)}
                        />
                    </>
                )}
                {step.eventType == 'StopOutputs' && (
                    <>
                        <FieldLabel htmlFor={`${stepIndex}speak`}>{t('common:event.stopOutputs.titleLong')}</FieldLabel>
                    </>
                )}
                {step.eventType == 'JoysticksMove' && (
                    <JoystickStep
                        event={step}
                        eventIndex={stepIndex}
                        macro={macro}
                        isPreview={isPreview}
                        onChange={onChange}
                    />
                )}

                {step.eventType == 'MouseMove' && (
                    <StepMouseMove
                        event={step}
                        eventIndex={stepIndex}
                        macro={macro}
                        isPreview={isPreview}
                        onChange={onChange}
                    />
                )}
                {hasHoldTime(step) && (
                    <>
                        <FormGroup>
                            <StyledTextField
                                fullWidth
                                label={t('common:event.hold')}
                                disabled={isPreview}
                                type="number"
                                value={step.holdTimeMilliseconds ?? 0}
                                id={`${stepIndex}hold`}
                                placeholder={t('common:event.hold')}
                                onChange={(e) => handleEventHoldChange(stepIndex, e.target.value)}
                            />
                        </FormGroup>
                    </>
                )}
                {showStepOptions ? (
                    <ChipButtonContainer style={{ marginTop: 32 }}>
                        <TextButton onClick={() => setShowStepOptions(false)}>
                            {t('common:event.hideOptions')}
                        </TextButton>
                        <TextButton
                            startIcon="moveUp"
                            onClick={() => handleMoveStepUp(stepIndex)}
                            disabled={stepIndex == 0}>
                            {t('common:event.stepUp')}
                        </TextButton>
                        <TextButton
                            startIcon="moveDown"
                            disabled={isPreview}
                            onClick={() => handleMoveStepDown(stepIndex)}>
                            {t('common:event.stepDown')}
                        </TextButton>
                        <TextButton
                            startIcon="duplicate"
                            disabled={isPreview}
                            onClick={() => handleCopyStep(stepIndex)}>
                            {t('common:event.stepCopy')}
                        </TextButton>
                        <TextButton
                            startIcon="insertRow"
                            disabled={isPreview}
                            onClick={() => handleInsertStep(stepIndex)}>
                            {t('common:event.insertStep')}
                        </TextButton>
                        <TextButton startIcon="delete" disabled={isPreview} onClick={() => handleRemoveStep(stepIndex)}>
                            {t('common:event.stepRemove')}
                        </TextButton>
                    </ChipButtonContainer>
                ) : (
                    <ChipButtonContainer style={{ marginTop: 8 }}>
                        <Button $color="simple" onClick={() => setShowStepOptions(true)}>
                            {t('common:event.showOptions')}
                        </Button>
                    </ChipButtonContainer>
                )}
            </StepCard>
        </>
    );
};
const StyledFormControlLabel = styled(FormControlLabel)`
    align-self: flex-start;
`;
const StepCard = styled(Card)`
    display: flex;
    flex-direction: column;
    justify-content: center;
    margin: 1rem auto;
    gap: 16px;
    padding: 32px 16px;
    width: calc(100% - 32px);
`;

const DeviceSubheader = styled(LabelLarge)`
    margin: 1rem 0.5rem;
`.withComponent('h3');

export default MacroStepEditor;
